import React from 'react';
import { Helmet } from 'react-helmet';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import {
  Bot,
  MessageSquare,
  Workflow,
  Sparkles,
  CheckCircle2,
  ArrowUpRight,
  Cpu,
  Database,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SafeJsonLd from '@/components/SafeJsonLd';
import { useLanguage } from '@/contexts/LanguageContext';
import ServicePricing, { PricingTier } from '@/components/service/ServicePricing';
import ServiceFAQ, { FAQItem } from '@/components/service/ServiceFAQ';
import ServiceCrossSell from '@/components/service/ServiceCrossSell';
import ServiceBreadcrumb from '@/components/service/ServiceBreadcrumb';

const ServicesAI: React.FC = () => {
  const { t, language } = useLanguage();
  const isPT = language === 'pt';

  const features = [
    { Icon: MessageSquare, key: 'chatbots' },
    { Icon: Workflow,      key: 'workflows' },
    { Icon: Sparkles,      key: 'content' },
  ] as const;

  const benefitsRaw = t('servicesAi.benefits') as unknown;
  const benefits: string[] = Array.isArray(benefitsRaw) ? (benefitsRaw as string[]) : [];

  const title = isPT
    ? 'Automação com IA para Pequenas Empresas | Spawncore'
    : 'AI Automation for Small Businesses | Spawncore';

  const description = isPT
    ? 'Implementação de automações com inteligência artificial: chatbots, workflows com n8n, atendimento 24/7 e automação de processos para pequenas empresas em Portugal.'
    : 'AI automation implementation: chatbots, n8n workflows, 24/7 customer service and process automation for small businesses in Portugal.';

  const tiers: PricingTier[] = isPT ? [
    {
      name: 'Spark',
      price: '€300',
      period: 'setup',
      description: 'Um chatbot ou automação simples no seu site ou WhatsApp.',
      features: [
        'Chatbot IA com FAQ da sua empresa',
        'Integração com email ou WhatsApp',
        '1 workflow automático',
        'Formação da equipa (1h)',
        '30 dias de ajustes incluídos',
      ],
      ctaLabel: 'Começar',
    },
    {
      name: 'Engine',
      price: '€1200',
      period: 'setup',
      description: 'Múltiplas automações ligadas — CRM, faturação, conteúdo.',
      features: [
        'Até 5 workflows com n8n / Make',
        'Chatbot avançado com memória',
        'Integrações WhatsApp, Gmail, CRM',
        'Painel de controlo e relatórios',
        'Formação + documentação completa',
        '90 dias de ajustes incluídos',
      ],
      popular: true,
      ctaLabel: 'Escolher Engine',
    },
    {
      name: 'Fleet',
      price: 'Sob proposta',
      description: 'Sistema de automação à medida para operações complexas.',
      features: [
        'Workflows ilimitados',
        'Agentes IA personalizados',
        'Integração com sistemas existentes',
        'Manutenção mensal dedicada',
        'SLA com tempo de resposta',
        'Auditoria trimestral',
      ],
      ctaLabel: 'Falar com o estúdio',
    },
  ] : [
    {
      name: 'Spark',
      price: '€300',
      period: 'setup',
      description: 'A simple chatbot or automation on your site or WhatsApp.',
      features: [
        'AI chatbot with your company FAQ',
        'Email or WhatsApp integration',
        '1 automated workflow',
        'Team training (1h)',
        '30 days of tweaks included',
      ],
      ctaLabel: 'Start small',
    },
    {
      name: 'Engine',
      price: '€1200',
      period: 'setup',
      description: 'Multiple connected automations — CRM, invoicing, content.',
      features: [
        'Up to 5 n8n / Make workflows',
        'Advanced chatbot with memory',
        'WhatsApp, Gmail, CRM integrations',
        'Control panel and reports',
        'Training + full documentation',
        '90 days of tweaks included',
      ],
      popular: true,
      ctaLabel: 'Choose Engine',
    },
    {
      name: 'Fleet',
      price: 'On request',
      description: 'Tailored automation system for complex operations.',
      features: [
        'Unlimited workflows',
        'Custom AI agents',
        'Integration with existing systems',
        'Dedicated monthly maintenance',
        'SLA with response time',
        'Quarterly audit',
      ],
      ctaLabel: 'Talk to the studio',
    },
  ];

  const faqs: FAQItem[] = isPT ? [
    {
      question: 'Que ferramentas usam para as automações?',
      answer: 'Trabalhamos sobretudo com n8n (open-source, podemos alojar no seu servidor para máxima privacidade), Make e Zapier. Para a parte de IA usamos OpenAI (GPT) e Anthropic (Claude). Em chatbots integramos com WhatsApp Business API, sites próprios, Messenger, Telegram e widgets web.',
    },
    {
      question: 'Os meus dados ficam seguros?',
      answer: 'Sim. Tudo o que envolva dados pessoais é tratado em conformidade com o RGPD. Quando possível, alojamos as automações no seu próprio servidor (n8n self-hosted) para que nada saia da sua infraestrutura. Documentamos cada fluxo de dados para garantir transparência.',
    },
    {
      question: 'E se o chatbot der uma resposta errada?',
      answer: 'Configuramos os chatbots com guard-rails claros (só responde dentro do âmbito definido) e com fallback para um humano quando não tem certeza. Pode rever todas as conversas e ajustar o comportamento ao longo do tempo. Não substituímos o seu atendimento — automatizamos a parte previsível.',
    },
    {
      question: 'Vou ficar dependente da Spawncore?',
      answer: 'Não. Toda a infraestrutura fica em seu nome (n8n no seu servidor, chaves de API na sua conta, automações documentadas). Pode levar tudo consigo a qualquer momento. Acreditamos em transparência total — o objetivo é dar autonomia, não criar lock-in.',
    },
    {
      question: 'Quanto tempo demora a primeira automação?',
      answer: 'Um chatbot simples ou um workflow básico fica pronto em 1-2 semanas. Projetos com várias integrações entram nos 3-4 semanas. Trabalhamos por sprints curtos com aprovação visível para nunca andar às escuras.',
    },
    {
      question: 'Posso começar pequeno?',
      answer: 'Recomendamos sempre começar pequeno. Escolha um processo que esteja a consumir tempo — atendimento, geração de propostas, agendamentos — e automatize só esse. Em 30 dias avaliamos resultados antes de expandir.',
    },
  ] : [
    {
      question: 'What tools do you use for the automations?',
      answer: 'Mostly n8n (open-source, can be self-hosted on your server for maximum privacy), Make and Zapier. For AI we use OpenAI (GPT) and Anthropic (Claude). For chatbots we integrate with WhatsApp Business API, your own site, Messenger, Telegram and web widgets.',
    },
    {
      question: 'Is my data safe?',
      answer: 'Yes. Anything involving personal data is handled in line with GDPR. When possible we host the automations on your own server (self-hosted n8n) so nothing leaves your infrastructure. Every data flow is documented for full transparency.',
    },
    {
      question: 'What if the chatbot gives a wrong answer?',
      answer: 'Chatbots are configured with clear guard-rails (only answers within scope) and human fallback when uncertain. You can review every conversation and tune the behaviour over time. We don\'t replace your support team — we automate the predictable part.',
    },
    {
      question: 'Will I be locked into Spawncore?',
      answer: 'No. All infrastructure stays in your name (self-hosted n8n, API keys on your account, documented workflows). You can take everything with you anytime. We believe in full transparency — the goal is autonomy, not lock-in.',
    },
    {
      question: 'How long until the first automation is live?',
      answer: 'A simple chatbot or basic workflow lands in 1-2 weeks. Multi-integration projects take 3-4 weeks. We work in short sprints with visible approvals so you\'re never in the dark.',
    },
    {
      question: 'Can I start small?',
      answer: 'We always recommend it. Pick one process that\'s eating time — support, proposals, scheduling — and automate just that. In 30 days we measure results before expanding.',
    },
  ];

  const useCases = isPT ? [
    { title: 'Restauração',    desc: 'Reservas automáticas no WhatsApp, lembretes a clientes 24h antes, e respostas a "abrem hoje?" sem alguém ao telefone.' },
    { title: 'Clínicas',       desc: 'Agendamento de consultas, recolha de informação pré-consulta, envio de exames e follow-up — tudo automatizado.' },
    { title: 'Lojas online',   desc: 'Atendimento pré-venda, recuperação de carrinhos abandonados e descrições de produto geradas com IA.' },
    { title: 'Serviços B2B',   desc: 'Qualificação automática de leads, agendamento no Calendar, propostas pré-preenchidas e CRM sempre atualizado.' },
    { title: 'Imobiliárias',   desc: 'Resposta imediata a interessados, agendamento de visitas e envio automático de plantas e dossiês.' },
    { title: 'Educação',       desc: 'Atendimento a encarregados, envio de avisos automáticos e relatórios de presença mensais.' },
  ] : [
    { title: 'Restaurants',    desc: 'Automatic WhatsApp bookings, customer reminders 24h prior, and answers to "are you open today?" without phone duty.' },
    { title: 'Clinics',        desc: 'Appointment scheduling, pre-consultation intake, exam delivery and follow-up — fully automated.' },
    { title: 'Online stores',  desc: 'Pre-sale chat, abandoned cart recovery and AI-generated product descriptions.' },
    { title: 'B2B services',   desc: 'Automatic lead qualification, Calendar scheduling, pre-filled proposals and always-up-to-date CRM.' },
    { title: 'Real estate',    desc: 'Instant replies to inquiries, viewing scheduling, automatic dispatch of floor plans and dossiers.' },
    { title: 'Education',      desc: 'Parent support, automated notices and monthly attendance reports.' },
  ];

  const techStack = [
    { Icon: Cpu,      name: 'n8n',         desc: isPT ? 'Workflows self-hosted'         : 'Self-hosted workflows' },
    { Icon: Workflow, name: 'Make',        desc: isPT ? 'Integrações cloud'             : 'Cloud integrations' },
    { Icon: Sparkles, name: 'OpenAI',      desc: 'GPT-4 / GPT-4o' },
    { Icon: Bot,      name: 'Anthropic',   desc: 'Claude Sonnet / Opus' },
    { Icon: MessageSquare, name: 'WhatsApp Business', desc: isPT ? 'API oficial' : 'Official API' },
    { Icon: Database, name: 'Supabase',    desc: isPT ? 'Base de dados & auth'          : 'Database & auth' },
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'AI Automation',
    name: isPT ? 'Automação com Inteligência Artificial' : 'AI Automation',
    description,
    provider: { '@type': 'Organization', name: 'Spawncore', url: 'https://spawncore.com' },
    areaServed: 'PT',
    audience: { '@type': 'BusinessAudience', audienceType: 'Small Business' },
    url: 'https://spawncore.com/services/ai',
    offers: tiers.map(t => ({
      '@type': 'Offer',
      name: t.name,
      price: t.price.replace(/[^\d]/g, '') || undefined,
      priceCurrency: 'EUR',
      description: t.description,
    })),
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={isPT ? 'pt' : 'en'} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={isPT
          ? 'automação IA Portugal, automação inteligência artificial, chatbots empresas, n8n consultoria, automação processos, IA pequenas empresas, agentes IA, workflows automatizados, chatbot WhatsApp, integração CRM'
          : 'AI automation Portugal, business chatbots, n8n consulting, workflow automation, AI for small business, AI agents, automated workflows, WhatsApp chatbot, CRM integration'} />
        <link rel="canonical" href="https://spawncore.com/services/ai" />
        <link rel="alternate" hrefLang="pt-PT" href="https://spawncore.com/services/ai" />
        <link rel="alternate" hrefLang="en" href="https://spawncore.com/services/ai" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spawncore.com/services/ai" />
        <meta property="og:image" content="https://spawncore.com/lovable-uploads/c121560e-1be0-4632-8106-cdadaf79cf04.png" />
        <meta property="og:locale" content={isPT ? 'pt_PT' : 'en_US'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>

      <SafeJsonLd data={serviceSchema} />

      <header>
        <Navigation />
      </header>

      <ServiceBreadcrumb label={t('nav.ai')} url="https://spawncore.com/services/ai" />

      <main>
        {/* Hero */}
        <section className="relative pt-12 pb-20 md:pt-20 md:pb-28 overflow-hidden">
          <div className="pointer-events-none absolute inset-0 mesh-bg opacity-70" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.35]" aria-hidden="true" />

          <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <span className="pill-tag-lime mb-8">
              <span className="h-1.5 w-1.5 rounded-full bg-lime animate-pulse" />
              AI Automation · {isPT ? 'Para pequenas empresas' : 'For small business'}
            </span>
            <h1 className="font-display text-foreground text-[clamp(2.5rem,7vw,5.5rem)] leading-[0.96] tracking-tight">
              {isPT ? 'Automação com ' : 'Automation that '}
              <span style={{ color: 'hsl(var(--lime))' }} className="italic font-display">
                {isPT ? 'Inteligência Artificial' : 'thinks for you'}
              </span>
            </h1>
            <p className="mt-7 text-lg md:text-2xl text-muted-foreground max-w-2xl leading-relaxed">
              {t('servicesAi.subtitle')}
            </p>
            <p className="mt-5 text-base text-muted-foreground/80 max-w-2xl leading-relaxed">
              {isPT
                ? 'Chatbots que respondem 24/7, agentes que qualificam leads, workflows que escrevem propostas e ligam todos os seus sistemas. Tecnologia de ponta — implementada à medida de empresas que valorizam tempo.'
                : 'Chatbots that answer 24/7, agents that qualify leads, workflows that draft proposals and connect every tool you use. Cutting-edge tech — fitted to businesses that value their time.'}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="group h-14 px-7 rounded-full bg-primary text-primary-foreground hover:bg-primary-light shadow-glow border border-primary-light/30 text-base w-full sm:w-auto"
                >
                  <Bot className="mr-2 h-4 w-4" />
                  {t('servicesAi.cta')}
                  <ArrowUpRight className="ml-2 h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Button>
              </Link>
              <a href="#pricing" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-7 rounded-full bg-card/40 border-border hover:bg-card text-base w-full sm:w-auto"
                >
                  {isPT ? 'Ver preços' : 'See pricing'}
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 md:py-28 bg-secondary/40 border-y border-border/60">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-14 max-w-3xl">
              <span className="eyebrow">— {t('servicesAi.whyUs')}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'Três frentes de automação inteligente.' : 'Three fronts of intelligent automation.'}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {features.map(({ Icon, key }, idx) => (
                <article
                  key={key}
                  className="group relative overflow-hidden rounded-2xl border border-border/80 bg-card/70 backdrop-blur-sm p-8 transition-all duration-500 hover:bg-card hover:border-lime/40 hover:-translate-y-1"
                >
                  <div className="pointer-events-none absolute -top-20 -right-20 h-48 w-48 rounded-full bg-lime/[0.08] blur-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
                  <div className="relative">
                    <div className="flex items-start justify-between mb-8">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-lime/30 bg-lime/[0.08] text-lime">
                        <Icon className="h-5 w-5" strokeWidth={1.6} />
                      </div>
                      <span className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70">
                        / 0{idx + 1}
                      </span>
                    </div>
                    <h3 className="font-display text-2xl md:text-[1.625rem] leading-tight tracking-tight mb-3 text-foreground">
                      {t(`servicesAi.features.${key}.title`)}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {t(`servicesAi.features.${key}.desc`)}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Use cases by industry */}
        <section className="py-20 md:py-28 bg-background">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-12 gap-6 mb-14">
              <div className="col-span-12 md:col-span-7">
                <span className="eyebrow">— {isPT ? 'Por indústria' : 'By industry'}</span>
                <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                  {isPT ? 'O que automatizámos em negócios como o seu.' : 'What we\'ve automated for businesses like yours.'}
                </h2>
              </div>
              <p className="col-span-12 md:col-span-4 md:col-start-9 self-end text-muted-foreground text-base md:text-lg leading-relaxed">
                {isPT
                  ? 'Cada setor tem padrões. Reaproveitamos o que já funcionou.'
                  : 'Every sector has patterns. We reuse what already works.'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {useCases.map((uc, idx) => (
                <article
                  key={uc.title}
                  className="group rounded-xl border border-border/80 bg-card/60 p-6 hover:bg-card hover:border-lime/30 transition-colors"
                >
                  <div className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70 mb-3">
                    / 0{idx + 1}
                  </div>
                  <h3 className="font-display text-xl text-foreground mb-2.5 leading-tight">{uc.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{uc.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits / Included */}
        <section className="py-20 md:py-28 bg-secondary/40 border-y border-border/60">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="mb-14 max-w-3xl">
              <span className="eyebrow">— {t('servicesAi.included')}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'O que está sempre incluído.' : 'What\'s always included.'}
              </h2>
            </div>
            <ul className="grid md:grid-cols-2 gap-x-12 gap-y-5">
              {benefits.map((benefit, idx) => (
                <li key={idx} className="flex items-start gap-4 group">
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-lime/30 bg-lime/[0.10] text-lime group-hover:bg-lime/[0.20] transition-colors">
                    <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                  </div>
                  <span className="text-foreground/90 text-base md:text-lg leading-relaxed pt-0.5">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Tech stack */}
        <section className="py-20 md:py-24 bg-background">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 max-w-3xl">
              <span className="eyebrow">— {isPT ? 'Stack' : 'Stack'}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'As ferramentas que usamos.' : 'Tools we use.'}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border/60 rounded-2xl overflow-hidden border border-border/60">
              {techStack.map(({ Icon, name, desc }) => (
                <div key={name} className="bg-card/60 p-5 hover:bg-card transition-colors">
                  <Icon className="h-5 w-5 text-lime mb-3" strokeWidth={1.6} />
                  <div className="font-display text-sm text-foreground leading-tight">{name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <div id="pricing">
          <ServicePricing
            eyebrow={isPT ? 'Investimento' : 'Investment'}
            heading={isPT ? 'Preços claros. Sem letras pequenas.' : 'Clear pricing. No fine print.'}
            subtitle={isPT
              ? 'Setup único + mensalidade opcional para manutenção e expansão. Sem fidelizações longas.'
              : 'One-off setup + optional monthly fee for maintenance and growth. No long lock-ins.'}
            tiers={tiers}
            note={isPT
              ? 'Inclui sempre infraestrutura aberta — n8n, OpenAI/Anthropic, WhatsApp Business — em contas suas. Sem lock-in.'
              : 'Always built on open infrastructure — n8n, OpenAI/Anthropic, WhatsApp Business — under your accounts. No lock-in.'}
          />
        </div>

        {/* FAQ */}
        <ServiceFAQ
          eyebrow={isPT ? 'Perguntas frequentes · IA' : 'FAQ · AI'}
          heading={isPT ? 'O que costumam perguntar sobre automação IA.' : 'What people ask about AI automation.'}
          subtitle={isPT ? 'Respostas honestas, sem hype.' : 'Honest answers, no hype.'}
          faqs={faqs}
        />

        {/* Final CTA */}
        <section className="py-20 md:py-28 bg-background relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 mesh-bg opacity-60" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />

          <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <span className="pill-tag-lime mb-6">
              <Sparkles className="h-3 w-3" />
              {isPT ? 'Chamada de 30 min · grátis' : 'Free 30-min call'}
            </span>
            <h2 className="font-display text-foreground text-[clamp(2rem,5vw,4rem)] leading-[1.02] tracking-tight mb-6">
              {t('servicesAi.ready')}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              {t('servicesAi.readyText')}
            </p>
            <Link to="/contact" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="group h-14 px-8 rounded-full bg-primary text-primary-foreground hover:bg-primary-light shadow-glow border border-primary-light/30 text-base w-full sm:w-auto"
              >
                <Bot className="mr-2 h-4 w-4" />
                {t('servicesAi.contact')}
                <ArrowUpRight className="ml-2 h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Button>
            </Link>
          </div>
        </section>

        <ServiceCrossSell current="ai" />
      </main>

      <Footer />
    </div>
  );
};

export default ServicesAI;
